import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthContext';
import { isApiError } from '../../api/client';
import { safeNext } from '../../lib/next';
import { Button } from '../../ui/Button';
import { Field, FormError } from '../../ui/Field';
import { AuthLayout } from './AuthLayout';

export function Login() {
  const { login, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) return <Navigate to={next} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!email.trim()) errs.email = 'Enter your email.';
    if (!password) errs.password = 'Enter your password.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await login({ email: email.trim(), password });
      // AuthContext flips isAuthenticated; the <Navigate> above sends us to `next`.
    } catch (err) {
      if (isApiError(err) && (err.status === 401 || err.status === 400)) {
        setErrors({ form: 'That email and password don’t match. Try again.' });
      } else {
        setErrors({ form: isApiError(err) ? err.message : 'Could not sign in. Try again.' });
      }
      setBusy(false);
    }
  }

  const signupHref = params.get('next') ? `/signup?next=${encodeURIComponent(next)}` : '/signup';

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your family lists."
      footer={
        <>
          New to Cady?{' '}
          <Link to={signupHref} className="font-semibold text-accent underline-offset-4 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <FormError>{errors.form}</FormError>
        <Field
          label="Email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          autoCapitalize="none"
          enterKeyHint="go"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          required
        />
        <Button type="submit" size="lg" block loading={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
